import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import {
  getOnboardingChecklist, getOnboardingRequests, getOnboardingCustomerReports,
  getReportLibrary, uploadReportDefinition, replaceReportDefinition, getReportVersions,
  archiveReportDefinition, restoreReportDefinition, downloadReportDefinition, setReportServes,
  downloadCustomerReportsZip,
} from '../../api'
import './AdminOnboarding.css'

// Route: /admin/onboarding. Three parts for one customer: the checklist (read-only, by
// phase), the RaaS report library with this customer's set, and the access requests.

const PHASE_LABEL = {
  setup: 'Setup',
  after_first_load: 'After the first load',
  gate: 'Gate before go-live',
  go_live: 'Go-live',
  ongoing: 'Ongoing',
  offboarding: 'Offboarding',
}

const WHO_LABEL = {
  customer_admin: 'Customer admin',
  ukg_case: 'UKG case',
  sempirical_onboarder: 'Onboarder',
  automatic: 'Automatic',
}

const STATUS_LABEL = { done: 'Done', missing: 'Missing', 'n/a': 'N/A', unknown: 'Not evaluated' }

// What a Cognos report specification is saved as (services/raas_report_library.py).
const ACCEPT = '.xml,.txt,.json'

const TABS = [
  { id: 'checklist', label: 'Checklist' },
  { id: 'reports', label: 'RaaS reports' },
  { id: 'requests', label: 'Access requests' },
]

function saveBlob({ blob, filename }) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function describeRule(rule) {
  if (!rule) return 'Every customer'
  const words = { products_any: 'Product', modules_any: 'Module' }
  return Object.entries(rule).map(([k, v]) => `${words[k] || k}: ${v.join(' or ')}`).join('; ')
}

function shortDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function useCopy() {
  const [copied, setCopied] = useState(null)
  async function copy(key, text) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(key)
      setTimeout(() => setCopied(c => (c === key ? null : c)), 2000)
    } catch {
      setCopied(`${key}:failed`)
    }
  }
  return [copied, copy]
}

// ── Checklist ────────────────────────────────────────────────────────────────────

function ChecklistItem({ item }) {
  return (
    <li className={`aob-item aob-item--${item.status.replace('/', '')}${item.applies === 'absent' ? ' aob-item--hidden' : ''}`}>
      <div className="aob-item-head">
        <span className={`aob-status aob-status--${item.status.replace('/', '')}`}>{STATUS_LABEL[item.status] || item.status}</span>
        <span className="aob-item-title">{item.title}</span>
        <span className="aob-item-id">{item.id}</span>
      </div>
      <div className="aob-item-meta">
        {(item.who || []).map(w => <span key={w} className="aob-chip">{WHO_LABEL[w] || w}</span>)}
        {item.applies_when_text && (
          <span className="aob-chip aob-chip--rule">Applies when {item.applies_when_text}</span>
        )}
        {item.applies === 'unknown' && (
          <span className="aob-chip aob-chip--undecided"
            title="No product key, declared system, connection or loaded data says yet whether this customer has it">
            Applies? Not known yet
          </span>
        )}
        {item.confirm && <span className="aob-chip aob-chip--confirm">Platform proposes, a person confirms</span>}
      </div>
      {item.why && <p className="aob-item-why">{item.why}</p>}
      <p className="aob-item-detail">{item.detail}</p>
      {item.gap && <p className="aob-item-gap"><strong>Gap:</strong> {item.gap}</p>}
    </li>
  )
}

function ChecklistPanel({ customerId }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showHidden, setShowHidden] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try { setData(await getOnboardingChecklist(customerId)) } catch (e) { setError(e); setData(null) } finally { setLoading(false) }
  }, [customerId])
  useEffect(() => { load() }, [load])

  if (error) return <LoadError what="the checklist" error={error} onRetry={load} />
  if (loading || !data) return <div className="aob-note">Evaluating the checklist…</div>

  const values = Object.entries(data.presence?.values || {})
  return (
    <div>
      <div className="aob-summary">
        {['done', 'missing', 'unknown', 'n/a'].map(k => (
          <div key={k} className={`aob-stat aob-stat--${k.replace('/', '')}`}>
            <div className="aob-stat-value">{data.counts?.[k] ?? 0}</div>
            <div className="aob-stat-label">{STATUS_LABEL[k]}</div>
          </div>
        ))}
        <div className="aob-presence">
          <div className="aob-presence-title">What this customer runs</div>
          <div className="aob-presence-list">
            {values.map(([v, info]) => (
              <span key={v} className={`aob-chip ${info.state === 'present' ? 'aob-chip--yes' : info.state === 'absent' ? 'aob-chip--no' : 'aob-chip--undecided'}`}
                title={info.detail || ''}>
                {v}: {info.state === 'present' ? 'yes' : info.state === 'absent' ? 'no' : 'not known yet'}
              </span>
            ))}
          </div>
          {data.presence?.product_keys_error && (
            <div className="aob-warn">The product keys could not be read ({data.presence.product_keys_error}), so nothing is hidden for want of them.</div>
          )}
          {!data.presence?.product_keys_error && !(data.presence?.product_keys || []).length && (
            <div className="aob-warn">No product keys loaded for this customer yet: what they run is read from connections, loaded data and the declared context only.</div>
          )}
        </div>
      </div>

      <label className="aob-toggle">
        <input type="checkbox" checked={showHidden} onChange={e => setShowHidden(e.target.checked)} />
        Show {data.hidden_count} item{data.hidden_count === 1 ? '' : 's'} that do not apply (the customer is known not to have what they name)
      </label>

      {(data.phases || []).map(ph => {
        const items = ph.items.filter(i => showHidden || i.applies !== 'absent')
        if (!items.length) return null
        return (
          <section key={ph.phase} className="aob-phase">
            <h3 className="aob-phase-title">{PHASE_LABEL[ph.phase] || ph.phase}
              <span className="aob-phase-count">{items.length}</span></h3>
            <ul className="aob-items">{items.map(i => <ChecklistItem key={i.id} item={i} />)}</ul>
          </section>
        )
      })}
      <p className="aob-foot">Read-only. Statuses come from the platform&apos;s own records (services/onboarding_status.py), the same evaluation as scripts/onboarding_status.py.</p>
    </div>
  )
}

// ── RaaS reports ─────────────────────────────────────────────────────────────────

function UploadForm({ expected, onDone }) {
  const [file, setFile] = useState(null)
  const [name, setName] = useState('')
  const [serves, setServes] = useState('checklist')
  const [custom, setCustom] = useState('{"modules_any": ["Recruiting"]}')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function submit(e) {
    e.preventDefault()
    if (!file) return
    setBusy(true); setError(null)
    try {
      const appliesWhen = serves === 'checklist' ? '' : serves === 'all' ? 'null' : custom
      await uploadReportDefinition({ file, reportName: name, appliesWhen })
      setFile(null); setName(''); e.target.reset()
      onDone()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="aob-upload" onSubmit={submit}>
      <div className="aob-upload-row">
        <label>Report name
          <input list="aob-expected" value={name} placeholder="defaults to the file name"
            onChange={e => setName(e.target.value)} className="aob-input" />
          <datalist id="aob-expected">
            {expected.filter(x => !x.uploaded).map(x => <option key={x.report_name} value={x.report_name} />)}
          </datalist>
        </label>
        <label>Serves
          <select value={serves} onChange={e => setServes(e.target.value)} className="aob-input">
            <option value="checklist">As the checklist says for this report</option>
            <option value="all">Every customer</option>
            <option value="custom">Custom rule</option>
          </select>
        </label>
        {serves === 'custom' && (
          <label>Rule (JSON)
            <input value={custom} onChange={e => setCustom(e.target.value)} className="aob-input aob-mono" />
          </label>
        )}
        <label>File
          <input type="file" accept={ACCEPT} onChange={e => setFile(e.target.files?.[0] || null)} className="aob-file" />
        </label>
        <button className="nav-btn primary" disabled={!file || busy}>{busy ? 'Uploading…' : 'Upload report'}</button>
      </div>
      {error && <div className="aob-error">{error}</div>}
    </form>
  )
}

function ServesEditor({ report, onSaved, onCancel }) {
  const [source, setSource] = useState(report.rule_source || 'checklist')
  const [custom, setCustom] = useState(JSON.stringify(report.applies_when || { modules_any: ['Recruiting'] }))
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function save(e) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      let rule = null
      if (source === 'custom') {
        try { rule = JSON.parse(custom) } catch { throw new Error('The custom rule is not valid JSON.') }
      }
      await setReportServes(report.id, source, rule, reason)
      onSaved()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="aob-serves" onSubmit={save}>
      <label>Serves
        <select className="aob-input" value={source} onChange={e => setSource(e.target.value)}>
          <option value="checklist">As the checklist says for this report</option>
          <option value="every">Every customer</option>
          <option value="custom">Custom rule</option>
        </select>
      </label>
      {source === 'custom' && (
        <label>Rule (JSON)
          <input className="aob-input aob-mono" value={custom} onChange={e => setCustom(e.target.value)} />
        </label>
      )}
      <label>Why
        <input className="aob-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="kept in the history" />
      </label>
      <div className="aob-serves-actions">
        <button className="nav-btn primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="nav-btn" onClick={onCancel}>Cancel</button>
      </div>
      {error && <div className="aob-error">{error}</div>}
    </form>
  )
}

function ReportRow({ report, onChanged }) {
  const [editing, setEditing] = useState(false)
  const [history, setHistory] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const fileRef = useRef(null)

  async function loadHistory() {
    try { setHistory((await getReportVersions(report.id)).versions || []) } catch (e) { setError(e.message) }
  }

  async function act(fn, after) {
    setBusy(true); setError(null)
    try { await fn(); if (after) await after(); onChanged() } catch (e) { setError(e.message) } finally { setBusy(false) }
  }

  function toggleHistory() {
    if (history) { setHistory(null); return }
    loadHistory()
  }

  function replace(e) {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (f) act(() => replaceReportDefinition(report.id, f), history ? loadHistory : null)
  }

  function archive() {
    const ok = window.confirm(`Archive "${report.report_name}"?\n\nIt leaves the library and every customer's download. Its row and every version stay stored, and Restore brings it back. Nothing is deleted.`)
    if (ok) act(() => archiveReportDefinition(report.id, 'archived from the Admin Onboarding page'))
  }

  const archived = !!report.archived_at
  return (
    <>
      <tr className={archived ? 'aob-row--archived' : ''}>
        <td className="aob-mono">{report.report_name}</td>
        <td>
          {describeRule(report.effective_applies_when)}
          {report.rule_note && <div className="aob-muted">{report.rule_note}</div>}
          {report.rule_set_by && <div className="aob-muted">Set by {report.rule_set_by}, {shortDate(report.rule_set_at)}</div>}
          {!archived && !editing && <button className="aob-link aob-link--inline" onClick={() => setEditing(true)}>Change</button>}
        </td>
        <td>v{report.current_version}</td>
        <td>{shortDate(report.uploaded_at)}<div className="aob-muted">{report.uploaded_by}</div></td>
        <td className="aob-actions">
          <button className="aob-link" onClick={() => downloadReportDefinition(report.id).then(saveBlob).catch(e => setError(e.message))}>Download</button>
          {!archived && (
            <>
              <button className="aob-link" disabled={busy} onClick={() => fileRef.current?.click()}>Replace</button>
              <input ref={fileRef} type="file" accept={ACCEPT} onChange={replace} hidden
                aria-label={`Replace the file for ${report.report_name}`} />
            </>
          )}
          <button className="aob-link" onClick={toggleHistory}>{history ? 'Hide history' : 'History'}</button>
          {archived
            ? <button className="aob-link" disabled={busy} onClick={() => act(() => restoreReportDefinition(report.id))}>Restore</button>
            : <button className="aob-link aob-link--danger" disabled={busy} onClick={archive}>Archive</button>}
        </td>
      </tr>
      {editing && (
        <tr className="aob-history"><td colSpan={5}>
          <ServesEditor report={report} onCancel={() => setEditing(false)}
            onSaved={() => { setEditing(false); if (history) loadHistory(); onChanged() }} />
        </td></tr>
      )}
      {error && <tr><td colSpan={5}><div className="aob-error">{error}</div></td></tr>}
      {history && (
        <tr className="aob-history"><td colSpan={5}>
          <table className="aob-subtable">
            <thead><tr><th>Version</th><th>File</th><th>Size</th><th>sha256</th><th>Uploaded</th><th></th></tr></thead>
            <tbody>
              {history.map(v => (
                <tr key={v.id}>
                  <td>v{v.version}{v.version === report.current_version ? ' (current)' : ''}</td>
                  <td className="aob-mono">{v.file_name}</td>
                  <td>{v.size_bytes?.toLocaleString()} B</td>
                  <td className="aob-mono aob-sha">{v.sha256?.slice(0, 12)}…</td>
                  <td>{shortDate(v.uploaded_at)} · {v.uploaded_by}</td>
                  <td><button className="aob-link" onClick={() => downloadReportDefinition(report.id, v.version).then(saveBlob).catch(e => setError(e.message))}>Download</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </td></tr>
      )}
    </>
  )
}

function ReportsPanel({ customerId, customerName }) {
  const [lib, setLib] = useState(null)
  const [mine, setMine] = useState(null)
  const [error, setError] = useState(null)
  const [showArchived, setShowArchived] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [zipError, setZipError] = useState(null)
  const [zipping, setZipping] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [l, m] = await Promise.all([getReportLibrary(showArchived), getOnboardingCustomerReports(customerId)])
      setLib(l); setMine(m)
    } catch (e) { setError(e) }
  }, [customerId, showArchived])
  useEffect(() => { load() }, [load])

  async function downloadZip() {
    setZipping(true); setZipError(null)
    try { saveBlob(await downloadCustomerReportsZip(customerId)) } catch (e) { setZipError(e.message) } finally { setZipping(false) }
  }

  if (error) return <LoadError what="the report library" error={error} onRetry={load} />
  if (!lib || !mine) return <div className="aob-note">Loading the report library…</div>

  return (
    <div className="aob-reports">
      <section className="aob-card">
        <div className="aob-card-head">
          <div>
            <h3 className="aob-card-title">{customerName}&apos;s reports</h3>
            <p className="aob-muted">Only the reports that apply to this customer&apos;s products and modules.</p>
          </div>
          <button className="nav-btn primary" onClick={downloadZip} disabled={zipping}>
            {zipping ? 'Building…' : `Download this customer's reports (${mine.reports.length})`}
          </button>
        </div>
        {zipError && <div className="aob-error">{zipError}</div>}
        {mine.library_empty && <div className="aob-warn">No report definitions uploaded yet. The download holds only the README with the import steps and the list below.</div>}
        {mine.reports.length > 0 && (
          <div className="aob-pills">{mine.reports.map(r => <span key={r.id} className={`aob-chip aob-mono ${r.applies_undecided ? 'aob-chip--undecided' : 'aob-chip--yes'}`} title={r.applies_undecided ? 'Applies? Not known yet: included so nothing is missed' : ''}>{r.report_name} v{r.current_version}{r.applies_undecided ? ' (applies? not known yet)' : ''}</span>)}</div>
        )}
        {mine.missing.length > 0 && (
          <div className="aob-missing">
            <div className="aob-missing-title">Applies to this customer, not uploaded yet ({mine.missing.length})</div>
            <div className="aob-pills">{mine.missing.map(m => <span key={m.report_name} className="aob-chip aob-chip--no aob-mono">{m.report_name}{m.applies_undecided ? ' (applies? not known yet)' : ''}</span>)}</div>
          </div>
        )}
        <h4 className="aob-steps-title">Import steps (by hand, as the customer&apos;s test user)</h4>
        <div className="aob-location">
          Report location: {mine.location?.recorded
            ? <span className="aob-mono">{mine.location.readable}</span>
            : <span className="aob-muted">{mine.location?.note || 'not recorded'}</span>}
        </div>
        <ol className="aob-steps">{mine.import_steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
      </section>

      <section className="aob-card">
        <div className="aob-card-head">
          <div>
            <h3 className="aob-card-title">Report library</h3>
            <p className="aob-muted">Our SEMP- report definitions. Replacing a file keeps every earlier version; nothing is deleted.</p>
          </div>
          <div className="aob-head-actions">
            <label className="aob-toggle aob-toggle--inline">
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
              Show archived ({lib.archived_count})
            </label>
            <button className="nav-btn" onClick={() => setShowUpload(s => !s)}>{showUpload ? 'Close' : '+ Upload report'}</button>
          </div>
        </div>
        {(showUpload || lib.empty) && <UploadForm expected={lib.expected || []} onDone={() => { setShowUpload(false); load() }} />}
        {lib.reports.length === 0 ? (
          <div className="aob-empty">No report definitions uploaded yet</div>
        ) : (
          <table className="aob-table">
            <thead><tr><th>Report</th><th>Serves</th><th>Version</th><th>Updated</th><th></th></tr></thead>
            <tbody>{lib.reports.map(r => <ReportRow key={r.id} report={r} onChanged={load} />)}</tbody>
          </table>
        )}
        <p className="aob-foot">
          {(lib.expected || []).filter(x => x.uploaded).length} of {(lib.expected || []).length} reports the checklist expects are uploaded.
        </p>
      </section>
    </div>
  )
}

// ── Access requests ──────────────────────────────────────────────────────────────

const PLACEHOLDER_RE = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g

// The body TEMPLATE with the paragraphs that apply and the ticked unknown ones, before the
// last paragraph; every placeholder still in it, filled later in one pass.
function composeTemplate(r, included) {
  const paras = r.paragraphs.filter(p => p.state === 'applies' || included[`${r.id}:${p.index}`]).map(p => p.template)
  return [r.parts_template.before, ...paras, r.parts_template.last].filter(Boolean).join('\n\n')
}

function neededBy(r, included) {
  return [...new Set([
    ...r.needs,
    ...r.paragraphs.filter(p => p.state === 'unknown' && included[`${r.id}:${p.index}`]).flatMap(p => p.needs || []),
  ])]
}

// Fills the editable placeholders the onboarder has a value for; any other {name} stays.
// The same rule as services/onboarding_requests.py fill: only {name} tokens whose name is an
// editable placeholder with a value are replaced; every other brace is left alone.
function fillIn(text, fields) {
  return text.replace(PLACEHOLDER_RE, (m, name) => (Object.hasOwn(fields, name) && String(fields[name] || '').trim() ? String(fields[name]).trim() : m))
}


function EditableFields({ data, fields, setFields, missing, included }) {
  const needed = Object.keys(data.editable || {}).filter(k => data.requests.some(r => neededBy(r, included).includes(k)))
  if (!needed.length) return null
  const contacts = (data.contacts || []).filter(c => c.name || c.email)
  function pickContact(idx) {
    const c = contacts[Number(idx)]
    if (!c) return
    setFields(f => ({ ...f, customer_contact_name: c.name, customer_contact_email: c.email, customer_contact_phone: c.phone }))
  }
  return (
    <div className="aob-fields">
      <div className="aob-fields-title">Fill these in before copying</div>
      {contacts.length > 0 && needed.some(k => k.startsWith('customer_contact_')) && (
        <label className="aob-field">
          <span>Use a declared contact</span>
          <select className="aob-input" defaultValue="" onChange={e => pickContact(e.target.value)}>
            <option value="" disabled>Choose a contact…</option>
            {contacts.map((c, i) => <option key={i} value={i}>{c.name || c.email}{c.role ? ` (${c.role})` : ''}</option>)}
          </select>
        </label>
      )}
      {needed.map(k => {
        const meta = data.editable[k]
        const empty = !(fields[k] || '').trim()
        return (
          <label key={k} className={`aob-field${empty && missing.has(k) ? ' aob-field--missing' : ''}`}>
            <span>{meta.label} <code className="aob-mono">{`{${k}}`}</code></span>
            <input className="aob-input" value={fields[k] || ''} placeholder="type it in"
              onChange={e => setFields(f => ({ ...f, [k]: e.target.value }))} />
            <small className="aob-muted">{meta.source ? `From ${meta.source}` : 'Nothing saved: type it in'}</small>
          </label>
        )
      })}
    </div>
  )
}

function RequestsPanel({ customerId }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [included, setIncluded] = useState({})
  const [fields, setFields] = useState({})
  const [copied, copy] = useCopy()

  const load = useCallback(async () => {
    setError(null)
    try {
      const d = await getOnboardingRequests(customerId)
      setData(d)
      setIncluded({})
      setFields(Object.fromEntries(Object.entries(d.editable || {}).map(([k, v]) => [k, v.value || ''])))
    } catch (e) { setError(e); setData(null) }
  }, [customerId])
  useEffect(() => { load() }, [load])

  if (error) return <LoadError what="the access requests" error={error} onRetry={load} />
  if (!data) return <div className="aob-note">Loading the access requests…</div>

  // Fixed values (customer name, our contact) and the editable fields, filled in ONE pass
  // over the template, so a value that contains braces is never read as a placeholder.
  const values = { ...(data.fixed || {}), ...fields }
  const subjectOf = r => fillIn(r.subject_template, values)
  const bodyOf = r => fillIn(composeTemplate(r, included), values)
  const textOf = r => `Subject: ${subjectOf(r)}\n\n${bodyOf(r)}`
  const editable = data.editable || {}
  const isMissing = name => !Object.hasOwn(editable, name) || !String(fields[name] || '').trim()
  const missingOf = r => neededBy(r, included).filter(isMissing)
  const allMissing = new Set(data.requests.flatMap(missingOf))
  const copyAll = data.requests.map(r => `[${r.audience_label}]\n${textOf(r)}`).join('\n\n----------------------------------------\n\n')

  return (
    <div>
      <div className="aob-card-head">
        <p className="aob-muted">
          {data.requests.length} request{data.requests.length === 1 ? '' : 's'} for {data.customer_name || customerId}:{' '}
          {data.ukg_case_count} UKG case{data.ukg_case_count === 1 ? '' : 's'} (the customer opens one case per request), the rest go to the customer.
        </p>
        <button className="nav-btn primary" disabled={allMissing.size > 0} onClick={() => copy('all', copyAll)}
          title={allMissing.size ? `Fill in ${[...allMissing].join(', ')} first` : ''}>
          {copied === 'all' ? 'Copied all' : copied === 'all:failed' ? 'Copy all failed: the browser refused the clipboard' : allMissing.size ? 'Copy all (fill in the fields first)' : 'Copy all'}
        </button>
      </div>
      <EditableFields data={data} fields={fields} setFields={setFields} missing={allMissing} included={included} />
      {data.requests.map(r => {
        const missing = missingOf(r)
        return (
          <article key={r.id} className={`aob-request${r.is_ukg_case ? ' aob-request--case' : ''}${missing.length ? ' aob-request--missing' : ''}`}>
            <div className="aob-request-head">
              <span className={`aob-badge ${r.is_ukg_case ? 'aob-badge--case' : 'aob-badge--admin'}`}>
                {r.is_ukg_case ? 'UKG case' : r.audience === 'customer_contact' ? 'Customer contact' : 'Customer admin'}
              </span>
              <span className="aob-request-title">{r.title}</span>
              {r.applies === 'unknown' && (
                <span className="aob-chip aob-chip--undecided" title={r.detail || ''}>Applies? Not known yet ({r.condition})</span>
              )}
              <button className="nav-btn" disabled={missing.length > 0} onClick={() => copy(r.id, textOf(r))}
                title={missing.length ? `Fill in ${missing.join(', ')} first` : ''}>
                {copied === r.id ? 'Copied' : copied === `${r.id}:failed` ? 'Copy failed' : 'Copy'}
              </button>
            </div>
            {missing.length > 0 && (
              <div className="aob-error">Not ready to copy: fill in {missing.map(m => `{${m}}`).join(', ')} above.</div>
            )}
            {r.summary && <p className="aob-request-summary">{r.summary}</p>}
            <div className="aob-muted aob-request-aud">Goes to {r.audience_label}{r.kb_article ? ` · cites ${r.kb_article}` : ''}</div>
            {r.paragraphs.some(p => p.state === 'unknown') && (
              <div className="aob-maybe">
                <div className="aob-maybe-title">Not known yet whether these apply. Tick the ones to include before copying:</div>
                {r.paragraphs.filter(p => p.state === 'unknown').map(p => {
                  const k = `${r.id}:${p.index}`
                  return (
                    <label key={k} className="aob-maybe-row">
                      <input type="checkbox" checked={!!included[k]} onChange={e => setIncluded(prev => ({ ...prev, [k]: e.target.checked }))} />
                      <span><strong>{p.condition}:</strong> {p.text}</span>
                    </label>
                  )
                })}
              </div>
            )}
            <div className="aob-subject"><span className="aob-muted">Subject:</span> {subjectOf(r)}</div>
            <pre className="aob-body">{bodyOf(r)}</pre>
          </article>
        )
      })}
      {data.not_applicable.length > 0 && (
        <details className="aob-na">
          <summary>{data.not_applicable.length} request{data.not_applicable.length === 1 ? '' : 's'} left out: the customer is known not to have what they name</summary>
          <ul>{data.not_applicable.map(r => <li key={r.id}>{r.title} <span className="aob-muted">({r.condition})</span></li>)}</ul>
        </details>
      )}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────────

export default function AdminOnboarding() {
  const [customerId, setCustomerId] = useState('')
  const { customers, error: customersError, reload } = useCustomerList(list => {
    if (list.length > 0) setCustomerId(id => id || list[0].customer_id || list[0].id)
  })
  const [tab, setTab] = useState('checklist')

  // Arrow keys, Home and End move between tabs (the WAI-ARIA tabs pattern).
  function onTabKey(e) {
    const i = TABS.findIndex(t => t.id === tab)
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: TABS.length - 1 }[e.key]
    if (next === undefined) return
    e.preventDefault()
    const t = TABS[(next + TABS.length) % TABS.length]
    setTab(t.id)
    document.getElementById(`aob-tab-${t.id}`)?.focus()
  }
  const current = customers.find(c => (c.customer_id || c.id) === customerId)
  const customerName = current?.company_name || current?.name || customerId

  const breadcrumbs = [{ label: 'Admin', href: '/admin' }, { label: 'Onboarding' }]
  const navActions = <Link to="/admin" className="nav-btn">← Dashboard</Link>

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
      <div className="aob-page">
        <div className="aob-header">
          <div>
            <h1 className="aob-title">Onboarding</h1>
            <p className="aob-subtitle">
              Where a customer stands against the onboarding checklist, the RaaS report definitions
              to load into their BI area once the test user&apos;s credentials arrive, and the access
              requests to hand them.
            </p>
          </div>
          <div className="aob-picker">
            <select className="aob-select" value={customerId} onChange={e => setCustomerId(e.target.value)} aria-label="Customer">
              {customers.map(c => (
                <option key={c.customer_id || c.id} value={c.customer_id || c.id}>{c.company_name || c.name || c.customer_id}</option>
              ))}
            </select>
            {customersError && <LoadError inline what="customers" error={customersError} onRetry={reload} />}
          </div>
        </div>

        <div className="aob-tabs" role="tablist" aria-label="Onboarding" onKeyDown={onTabKey}>
          {TABS.map(t => (
            <button key={t.id} id={`aob-tab-${t.id}`} role="tab" aria-selected={tab === t.id}
              aria-controls={tab === t.id && customerId ? `aob-panel-${t.id}` : undefined} tabIndex={tab === t.id ? 0 : -1}
              className={`aob-tab${tab === t.id ? ' aob-tab--active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>

        {!customerId ? <div className="aob-note">Choose a customer.</div> : (
          <>
            <div role="tabpanel" id={`aob-panel-${tab}`} aria-labelledby={`aob-tab-${tab}`} tabIndex={0}>
              {tab === 'checklist' && <ChecklistPanel key={customerId} customerId={customerId} />}
              {tab === 'reports' && <ReportsPanel key={customerId} customerId={customerId} customerName={customerName} />}
              {tab === 'requests' && <RequestsPanel key={customerId} customerId={customerId} />}
            </div>
          </>
        )}
      </div>
    </>
  )
}
